import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, AuthContext } from "../App";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "./ui/button";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refreshUser } = useContext(AuthContext);
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= 12) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const res = await fetch(`${API}/billing/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const d = await res.json();
        if (d.payment_status === "paid") {
          await refreshUser();
          setState("success");
          setTimeout(() => navigate("/dashboard"), 2200);
          return;
        }
        if (d.status === "expired") {
          setState("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      setTimeout(poll, 2500);
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 flex items-center justify-center px-4" data-testid="billing-success-page">
      <div className="bg-white rounded-2xl shadow-xl border p-8 text-center max-w-md">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-600 animate-spin mx-auto" />
            <h1 className="text-xl font-bold text-slate-900 mt-4">Setting up your account…</h1>
            <p className="text-slate-500 mt-2">Confirming your payment with Stripe. This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" data-testid="billing-success-icon" />
            <h1 className="text-2xl font-bold text-slate-900 mt-3">You're subscribed!</h1>
            <p className="text-slate-600 mt-2">Your trial limits are removed and your data is safe. Redirecting…</p>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-600 mx-auto" />
            <h1 className="text-xl font-bold text-slate-900 mt-4">Still processing</h1>
            <p className="text-slate-500 mt-2">Your payment is being confirmed. You can continue — it'll update shortly.</p>
            <Button className="mt-4 bg-amber-500 hover:bg-amber-600" onClick={() => navigate("/dashboard")} data-testid="billing-continue-btn">Go to Dashboard</Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-rose-600 mx-auto" />
            <h1 className="text-xl font-bold text-slate-900 mt-4">Something went wrong</h1>
            <p className="text-slate-500 mt-2">We couldn't confirm this payment. Please try again or contact support.</p>
            <Button className="mt-4" variant="outline" onClick={() => navigate("/billing")}>Back to Billing</Button>
          </>
        )}
      </div>
    </div>
  );
}
